import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Toaster, toast } from "react-hot-toast";
import {
  GetAllFollowUpTypes,
  fetchProjectData,
  GetLeadLog,
  fetchLeadData,
  createUserTask,
} from "../Services/APIService";
import Select from "react-select";

interface LeadDetailsViewProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (contactData: CreateUserTaskRequest) => void;
  preselectedLeadId?: number | null;
}

export interface followupTypeList {
  id: number;
  name: string;
  isActive: boolean;
}

export interface Project {
  projectID: number;
  projectName: string;
}

export interface Leads {
  leadId: number;
  firstName: string;
  lastName: string;
}

export interface LeadLgoList {
  logId: number;
  leadId: number;
  isNextActionRequired: boolean;
  taskId: number | null;
  assignedUser: number;
}

interface SelectOption {
  value: number;
  label: string;
  projectData?: Project;
}

interface SelectLeadOption {
  value: number;
  label: string;
  leadData: Leads;
  logId: number;
}

interface FollowUpTypeOption {
  value: number;
  label: string;
}

interface FormData {
  reportTo: SelectOption | null;
  note: string;
  followupDate: string;
  leadData: SelectLeadOption | null;
  followUpType: FollowUpTypeOption | null;
}

export interface CreateUserTaskRequest {
  leadId: number;
  logId: number;
  projectId?: number;
  assignedUser: number;
  followupTypeId: number;
  followupDate: string;
  taskNotes: string;
  tenantId: number;
  createdBy: number;
  projectName?: string;
}

interface ValidationErrors {
  reportTo?: string;
  note?: string;
  followupDate?: string;
  leadData?: string;
  followUpType?: string;
}

const UserTask: React.FC<LeadDetailsViewProps> = ({
  isOpen,
  onClose,
  onSubmit,
  preselectedLeadId,
}) => {
  const [followupType, setFollowupType] = useState<followupTypeList[]>([]);
  const [projectData, setprojectData] = useState<Project[]>([]);
  const [projectOption, setProjectOption] = useState<SelectOption[]>([]);
  const [followUpTypeOption, setFollowUpTypeOption] = useState<
    FollowUpTypeOption[]
  >([]);
  const [allowCreateTaskLeadOption, setallowCreateTaskLeadOption] = useState<
    SelectLeadOption[]
  >([]);
  const [leadLogData, setLeadLogData] = useState<LeadLgoList[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [formData, setFormData] = useState<FormData>({
    reportTo: null,
    note: "",
    followupDate: "",
    leadData: null,
    followUpType: null,
  });
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const getFetchpMasteData = async () => {
      setIsLoading(true);

      try {
        // Fetch followup types
        const fetchAllFolowUpType = await GetAllFollowUpTypes();
        const activeTypes = fetchAllFolowUpType.filter(
          (type: followupTypeList) =>
            type.isActive &&
            type.id !== 0 &&
            type.name.toLowerCase() !== "no followup" &&
            type.name.toLowerCase() !== "note"
        );
        setFollowupType(activeTypes);

        const followUpOptions = activeTypes.map((type) => ({
          value: type.id,
          label: type.name,
        }));
        setFollowUpTypeOption(followUpOptions);

        // Fetch project
        const fetchAllProjectData = await fetchProjectData();
        const ActiveProjectData = fetchAllProjectData.filter(
          (project) => project.isActive === true
        );
        const options = ActiveProjectData.map((project) => ({
          value: project.projectID,
          label: project.projectName,
          projectData: project,
        }));
        setProjectOption(options);
        setprojectData(ActiveProjectData);

        // Fetch LeadLogData
        const leadLogs = await GetLeadLog();
        const allowCreateTaskLead = leadLogs.filter(
          (log) => log.isNextActionRequired === true && log.taskId === null
        );
        const leadIds = allowCreateTaskLead.map((log) => log.leadId);
        setLeadLogData(allowCreateTaskLead);

        // Get all lead data
        const fetchAllLeadData = await fetchLeadData();

        const matchedLeads = fetchAllLeadData.filter((lead) =>
          leadIds.includes(lead.leadId)
        );

        // Updated dropdown options to include logId and leadData
        const dropdownOptions = matchedLeads.map((lead) => {
          const correspondingLog = allowCreateTaskLead.find(
            (log) => log.leadId === lead.leadId
          );

          return {
            value: lead.leadId,
            label: `${lead.firstName} ${lead.lastName ? null : ""}`,
            leadData: lead,
            logId: correspondingLog?.logId || 0,
          };
        });

        setallowCreateTaskLeadOption(dropdownOptions);

        if (preselectedLeadId) {
          const preselectedLead = dropdownOptions.find(
            (opt) => opt.value === preselectedLeadId
          );
          if (preselectedLead) {
            setFormData((prev) => ({
              ...prev,
              leadData: preselectedLead,
            }));
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    getFetchpMasteData();
  }, [preselectedLeadId]);

  const handleSelectChange = (
    name: string,
    selectedOption: SelectOption | FollowUpTypeOption | SelectLeadOption | null
  ) => {
    setFormData((prev) => ({
      ...prev,
      [name]: selectedOption,
    }));
    if (selectedOption && errors[name as keyof ValidationErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (value && errors[name as keyof ValidationErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleDateTimeChange = (e: any) => {
    const selectedDateTime = new Date(e.target.value);
    const currentDateTime = new Date();

    if (selectedDateTime < currentDateTime) {
      // Show error or reset to current time
      toast.error("Please select a future date and time");
      return;
    }

    handleInputChange(e);
  };

  const getLocalStorageValue = (key: string): number => {
    const value = localStorage.getItem(key);
    return value ? parseInt(value, 10) : 0;
  };

  const validateForm = (): { isValid: boolean; errors: ValidationErrors } => {
    const newErrors: ValidationErrors = {};
    let isValid = true;

    if (!formData.followUpType) {
      newErrors.followUpType = "Follow-up type is required";
      isValid = false;
    }
    if (!formData.leadData) {
      newErrors.leadData = "Lead selection is required";
      isValid = false;
    }
    if (!formData.followupDate) {
      newErrors.followupDate = "Followup date is required";
      isValid = false;
    }
    if (!formData.note.trim()) {
      newErrors.note = "Task note is required";
      isValid = false;
    }
    if (
      ![2, 3, 4, 5].includes(formData.followUpType?.value ?? -1) &&
      !formData.reportTo
    ) {
      newErrors.reportTo = "Project selection is required";
      isValid = false;
    }

    return { isValid, errors: newErrors };
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      const validation = validateForm();
      if (!validation.isValid) {
        setErrors(validation.errors);
        console.error("Form validation failed:", validation.errors);
        return;
      }

      const assignedUser =
        getLocalStorageValue("assignedUser") || getLocalStorageValue("UserId");
      const tenantId = getLocalStorageValue("tenantId");
      const createdBy =
        getLocalStorageValue("createdBy") || getLocalStorageValue("UserId");
      const selectedLeadId = formData.leadData?.value || 0;
      const selectedLogId = formData.leadData?.logId || 0;
      const selectedLeadLogData = leadLogData.find(
        (x) => x.leadId === selectedLeadId
      );

      const requestData: CreateUserTaskRequest = {
        leadId: selectedLeadId,
        logId: selectedLogId,
        projectId: formData.reportTo?.value ?? 0,
        assignedUser: selectedLeadLogData?.assignedUser || assignedUser,
        followupTypeId: formData.followUpType?.value ?? 0,
        followupDate: formData.followupDate,
        taskNotes: formData.note,
        tenantId: tenantId,
        createdBy: createdBy,
      };

      const result = await createUserTask(requestData);
      console.log("Task created successfully:", result);

      if (result.isCreated) {
        onSubmit(requestData);
        onClose();
        setTimeout(() => {
          navigate("/UserTaskView", { state: { userDataUpdated: true } });
        }, 1000);
      }

      setFormData({
        reportTo: null,
        note: "",
        followupDate: "",
        leadData: null,
        followUpType: null,
      });
    } catch (error) {
      console.error("Error submitting task:", error);
      alert("Error creating task. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDateTimeLocal = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  useEffect(() => {
    if (!formData.followupDate) {
      const now = new Date();
      setFormData((prev) => ({
        ...prev,
        followupDate: formatDateTimeLocal(now),
      }));
    }
  }, []);

  const handleClose = () => {
    setFormData({
      reportTo: null,
      note: "",
      followupDate: "",
      leadData: null,
      followUpType: null,
    });
    setErrors({});
    onClose();
  };

  const requiredField = <span className="text-danger ms-1">*</span>;

  return (
    <>
      <Toaster
        position="top-center"
        reverseOrder={false}
        containerStyle={{
          zIndex: 10000,
        }}
      />
      <div
        className={`modal-backdrop-custom ${isOpen ? "open" : "closed"}`}
        onClick={handleClose}
      ></div>

      <div className={`modal-custom ${isOpen ? "open" : "closed"}`}>
        <div className="modal-content usertaskCreatFormContent">
          <div className="modal-header-custom">
            <h5 className="modal-title-custom">Create New Follow-up</h5>
            <button
              type="button"
              className="btn-close-custom"
              onClick={handleClose}
            >
              <span className="icon-close"></span>
            </button>
          </div>

          <div className="modal-body-custom">
            <div className="card pop-up-card">
              <div className="card-body p-0 overflow-hidden">
                <div className="modal-body-content">
                  <form>
                    <div className="row">
                      <div className="col-12 col-lg-6 mb-3 input-group-custom">
                        <label className="form-label">
                          Follow-up Type{requiredField}
                        </label>
                        <Select
                          options={followUpTypeOption}
                          value={formData.followUpType}
                          onChange={(option) =>
                            handleSelectChange("followUpType", option)
                          }
                          className={`basic-single-selectm ${
                            errors.followUpType ? "is-invalid" : ""
                          }`}
                          classNamePrefix="select"
                          placeholder="Select follow-up type"
                          isLoading={isLoading}
                          isClearable
                          isSearchable
                        />
                        {errors.followUpType && (
                          <div className="invalid-feedback-custom">
                            {errors.followUpType}
                          </div>
                        )}
                      </div>
                      <div className="col-12 col-lg-6 mb-3 input-group-custom">
                        <label className="form-label">
                          Select Project{" "}
                          {![2, 3, 4, 5].includes(
                            formData.followUpType?.value ?? -1
                          ) && requiredField}
                        </label>
                        <Select
                          options={projectOption}
                          value={formData.reportTo}
                          onChange={(option) =>
                            handleSelectChange("reportTo", option)
                          }
                          className={`basic-single-selectm ${
                            errors.reportTo ? "is-invalid" : ""
                          }`}
                          classNamePrefix="select"
                          placeholder="Select project"
                          isLoading={isLoading}
                          isClearable
                          isSearchable
                          required
                        />
                        {errors.reportTo && (
                          <div className="invalid-feedback-custom">
                            {errors.reportTo}
                          </div>
                        )}
                      </div>
                      <div className="col-12 col-lg-6 mb-3 input-group-custom">
                        <label className="form-label">
                          Select Lead{requiredField}
                        </label>
                        <Select
                          options={allowCreateTaskLeadOption}
                          value={formData.leadData}
                          onChange={(option) =>
                            handleSelectChange("leadData", option)
                          }
                          className={`basic-single-selectm ${
                            errors.leadData ? "is-invalid" : ""
                          }`}
                          classNamePrefix="select"
                          placeholder="Select Lead"
                          isLoading={isLoading}
                          isClearable
                          isSearchable
                        />
                        {errors.leadData && (
                          <div className="invalid-feedback-custom">
                            {errors.leadData}
                          </div>
                        )}
                      </div>
                      <div className="col-12 col-lg-6 mb-3 input-group-custom">
                        <label htmlFor="followupDate" className="form-label">
                          Followup Date{requiredField}
                        </label>
                        <div className="input-with-icon">
                          <input
                            type="datetime-local"
                            id="followupDate"
                            name="followupDate"
                            value={formData.followupDate || ""}
                            onChange={handleDateTimeChange}
                            min={new Date().toISOString().slice(0, 16)}
                            className={`form-control-custom1 form-control-custom-lg ${
                              errors.followupDate ? "is-invalid" : ""
                            }`}
                          />
                          {errors.followupDate && (
                            <div className="invalid-feedback-custom">
                              {errors.followupDate}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-12 mb-3 input-group-custom">
                        <label htmlFor="note" className="form-label">
                          Task Note{requiredField}
                        </label>
                        <div className="input-with-icon">
                          <span className="input-icon icon-note"></span>
                          <textarea
                            onChange={handleInputChange}
                            id="note"
                            name="note"
                            className={`form-control-custom-textarea ${
                              errors.note ? "is-invalid" : ""
                            }`}
                            placeholder="Enter Task Note"
                            value={formData.note}
                            rows={4}
                          />
                          {errors.note && (
                            <div className="invalid-feedback-custom">
                              {errors.note}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>

            <div className="modal-footer-custom">
              <div>
                <button
                  type="button"
                  onClick={handleClose}
                  className="btn-custom btn-cancel"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-custom btn-primary-custom"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Submitting..." : "Submit Task"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default UserTask;
